import type { ChunkGraph, ChunkRef } from './chunkGraph';
import type { Loop } from './types';

/**
 * One selected thing.
 *
 * Two granularities, matching the vocabulary: a whole stitched chain, or the run
 * of segments one entity contributed to it. Outside mapping mode only `loop` refs
 * are produced, but the shape is the same either way so nothing downstream has to
 * care which mode made the selection.
 */
export type SelectionRef =
  | { readonly kind: 'loop'; readonly loopId: number }
  | {
      readonly kind: 'edge';
      readonly loopId: number;
      readonly chunkIndex: number;
    };

/**
 * Stable string key for a ref, so selections can live in a `Map` and keep
 * insertion order — which is what makes "the first thing you picked" meaningful.
 */
export const selectionKey = (ref: SelectionRef): string =>
  ref.kind === 'loop' ? `L${ref.loopId}` : `E${ref.loopId}:${ref.chunkIndex}`;

export type Selection = ReadonlyMap<string, SelectionRef>;

export const EMPTY_SELECTION: Selection = new Map();

const loopKeyOf = (loopId: number): string =>
  selectionKey({ kind: 'loop', loopId });

const edgeKeyOf = (loopId: number, chunkIndex: number): string =>
  selectionKey({ kind: 'edge', loopId, chunkIndex });

const dropEdgesOf = (next: Map<string, SelectionRef>, loop: Loop): boolean => {
  let dropped = false;
  loop.chunks.forEach((chunk) => {
    if (next.delete(edgeKeyOf(loop.id, chunk.index))) {
      dropped = true;
    }
  });
  return dropped;
};

/**
 * True when `selection` already names the same geometry as `ref`, even if the
 * two disagree on granularity — a loop covers every one of its edges, and a
 * complete set of those edges covers the loop.
 */
export const selectionCoversRef = (
  selection: Selection,
  ref: SelectionRef,
  loops?: readonly Loop[],
): boolean => {
  if (selection.has(selectionKey(ref))) {
    return true;
  }
  if (!loops) {
    return false;
  }
  if (ref.kind === 'edge') {
    return selection.has(loopKeyOf(ref.loopId));
  }
  const loop = loops.find((candidate) => candidate.id === ref.loopId);
  return (
    loop !== undefined &&
    loop.chunks.length > 0 &&
    loop.chunks.every((chunk) => selection.has(edgeKeyOf(loop.id, chunk.index)))
  );
};

/** Adds refs, ignoring ones already present. Returns the input if nothing changed. */
export const addToSelection = (
  selection: Selection,
  refs: readonly SelectionRef[],
  loops?: readonly Loop[],
): Selection => {
  if (refs.length === 0) {
    return selection;
  }
  const byLoopId = loops && new Map(loops.map((loop) => [loop.id, loop]));
  const next = new Map(selection);
  let changed = false;

  refs.forEach((ref) => {
    const key = selectionKey(ref);
    if (next.has(key)) {
      return;
    }

    const loop = byLoopId?.get(ref.loopId);
    if (loop) {
      if (ref.kind === 'edge' && next.has(loopKeyOf(ref.loopId))) {
        // The parent loop is already selected whole — this edge is in it.
        return;
      }
      if (ref.kind === 'loop') {
        // Collapse any leftover edge refs of this loop so the selection holds
        // one loop entry rather than a loop stacked on its own chunks.
        dropEdgesOf(next, loop);
        next.set(key, ref);
        changed = true;
        return;
      }
    }

    next.set(key, ref);
    changed = true;
  });

  return changed ? next : selection;
};

/** Removes refs. Returns the input if none were present. */
export const removeFromSelection = (
  selection: Selection,
  refs: readonly SelectionRef[],
  loops?: readonly Loop[],
): Selection => {
  if (refs.length === 0) {
    return selection;
  }
  const byLoopId = loops && new Map(loops.map((loop) => [loop.id, loop]));
  const next = new Map(selection);
  let changed = false;

  refs.forEach((ref) => {
    const key = selectionKey(ref);
    const loop = byLoopId?.get(ref.loopId);

    if (next.has(key)) {
      next.delete(key);
      changed = true;
      if (ref.kind === 'loop' && loop) {
        dropEdgesOf(next, loop);
      }
      return;
    }

    if (!loop) {
      return;
    }

    if (ref.kind === 'edge' && next.has(loopKeyOf(ref.loopId))) {
      // The loop is selected whole; break it into its other edges so subtracting
      // this one does not silently no-op.
      next.delete(loopKeyOf(ref.loopId));
      loop.chunks.forEach((chunk) => {
        if (chunk.index === ref.chunkIndex) {
          return;
        }
        const edge: SelectionRef = {
          kind: 'edge',
          loopId: ref.loopId,
          chunkIndex: chunk.index,
        };
        next.set(selectionKey(edge), edge);
      });
      changed = true;
      return;
    }

    if (ref.kind === 'loop' && dropEdgesOf(next, loop)) {
      // The loop was stored as individual edges. Dropping those is the subtract.
      changed = true;
    }
  });

  if (!changed) {
    return selection;
  }
  // Empty is interned, so "nothing is selected" is one value rather than an
  // endless supply of distinct empty maps. Callers compare selections by
  // reference — the accent builder to decide whether to rebuild, the mapping undo
  // stack to decide whether two steps describe the same thing — and without this
  // two selections that are both empty compare as different.
  return next.size === 0 ? EMPTY_SELECTION : next;
};

/**
 * Adds refs that are absent and removes ones that are present.
 *
 * `loops` lets this see through a granularity mismatch: Alt forces edge-granularity
 * picking regardless of how the geometry is currently selected, so a Shift+Alt
 * click can hand this an edge ref while the selection only holds the *loop* ref
 * that covers it (or the reverse — a loop ref while the selection holds every one
 * of its edges individually). Without `loops`, a plain key lookup misses in
 * both cases and adds a duplicate instead of removing what is already selected.
 * When `loops` isn't given, this falls back to the exact-key behaviour above.
 */
export const toggleInSelection = (
  selection: Selection,
  refs: readonly SelectionRef[],
  loops?: readonly Loop[],
): Selection => {
  if (refs.length === 0) {
    return selection;
  }
  const byLoopId = loops && new Map(loops.map((loop) => [loop.id, loop]));
  const next = new Map(selection);
  refs.forEach((ref) => {
    const key = selectionKey(ref);
    if (next.has(key)) {
      next.delete(key);
      return;
    }

    const loop = byLoopId?.get(ref.loopId);
    if (loop) {
      const loopKey = selectionKey({ kind: 'loop', loopId: ref.loopId });
      if (ref.kind === 'edge' && next.has(loopKey)) {
        // The loop is selected whole; break it into its edges minus this one, so
        // the click reads as "deselect this edge" instead of a no-op that grows
        // the selection.
        next.delete(loopKey);
        loop.chunks.forEach((chunk) => {
          if (chunk.index !== ref.chunkIndex) {
            next.set(
              selectionKey({
                kind: 'edge',
                loopId: ref.loopId,
                chunkIndex: chunk.index,
              }),
              {
                kind: 'edge',
                loopId: ref.loopId,
                chunkIndex: chunk.index,
              },
            );
          }
        });
        return;
      }
      if (ref.kind === 'loop') {
        const selectedEdgeCount = loop.chunks.filter((chunk) =>
          next.has(edgeKeyOf(ref.loopId, chunk.index)),
        ).length;
        if (selectedEdgeCount > 0) {
          dropEdgesOf(next, loop);
          if (selectedEdgeCount === loop.chunks.length) {
            // Every edge is already selected individually — the same geometry,
            // addressed the other way. Toggling the loop ref should undo that,
            // not add a redundant loop entry on top.
            return;
          }
          // A partial edge selection of this loop. Clicking the loop upgrades
          // those edges to the whole loop rather than stacking a loop ref on top.
          next.set(key, ref);
          return;
        }
      }
    }

    next.set(key, ref);
  });
  // Interned when it lands on empty, for the same reason as `removeFromSelection`:
  // shift-clicking the last selected item away must produce the same "nothing"
  // that clearing does.
  return next.size === 0 ? EMPTY_SELECTION : next;
};

/** Builds a selection from refs, preserving order and dropping duplicates. */
export const selectionOf = (refs: readonly SelectionRef[]): Selection =>
  addToSelection(EMPTY_SELECTION, refs);

/**
 * What a gesture does to the selection it found already there.
 *
 * One vocabulary for every gesture that selects — a click, a marquee release, a
 * click on a layer or mapping row — because they differ only in *which* refs they
 * name, never in how those refs combine with what was already picked.
 *
 * `toggle` is per-ref: it is what Shift means on the canvas, where each caught
 * item is its own thing. A panel row is a single thing whose refs are an
 * implementation detail, so a row resolves Shift to `add` or `subtract` against
 * its own selected state rather than toggling its refs one by one — see
 * `panelRowMergeMode`.
 */
export type SelectionMergeMode = 'replace' | 'add' | 'subtract' | 'toggle';

/**
 * Applies `refs` to `selection` under `mode`.
 *
 * The one place the four modes are dispatched. Returns the input reference when
 * nothing changed, which every one of the underlying primitives already
 * guarantees — callers compare selections by reference to decide whether to
 * rebuild geometry or record an undo step.
 */
export const mergeSelection = (
  selection: Selection,
  refs: readonly SelectionRef[],
  mode: SelectionMergeMode,
  loops?: readonly Loop[],
): Selection => {
  switch (mode) {
    case 'add':
      return addToSelection(selection, refs, loops);
    case 'subtract':
      return removeFromSelection(selection, refs, loops);
    case 'toggle':
      return toggleInSelection(selection, refs, loops);
    case 'replace':
    default:
      return selectionOf(refs);
  }
};

/**
 * Drops whatever a hidden layer took with it.
 *
 * Pruning rather than clearing, per the spec: hiding one layer leaves selections
 * on other layers alone. Because stitching runs across layers, a loop counts as
 * gone only when *every* chunk of it is hidden — a loop with one chunk on a hidden
 * layer is still visible, still pickable, and so stays selected. An edge ref goes
 * as soon as its own chunk is hidden.
 *
 * Nothing is remembered, so re-showing the layer does not bring the selection back.
 */
export const pruneHiddenFromSelection = (
  selection: Selection,
  loops: readonly Loop[],
  hiddenLayers: ReadonlySet<number>,
): Selection => {
  if (selection.size === 0) {
    return selection;
  }

  const byId = new Map(loops.map((loop) => [loop.id, loop]));
  const doomed: SelectionRef[] = [];

  selection.forEach((ref) => {
    const loop = byId.get(ref.loopId);
    if (!loop) {
      doomed.push(ref);
      return;
    }
    if (ref.kind === 'edge') {
      const chunk = loop.chunks[ref.chunkIndex];
      if (!chunk || hiddenLayers.has(chunk.layer)) {
        doomed.push(ref);
      }
      return;
    }
    const isEntirelyHidden = loop.chunks.every((chunk) =>
      hiddenLayers.has(chunk.layer),
    );
    if (isEntirelyHidden) {
      doomed.push(ref);
    }
  });

  return removeFromSelection(selection, doomed);
};

/** What the details overlay reports about a selection. */
export type SelectionSummary = {
  /** Number of selected items, at whatever granularity they were picked. */
  count: number;
  /** Total path length, in drawing units. */
  length: number;
  /**
   * Total enclosed area, or `null` when nothing closed contributes one. `null`
   * rather than `0` so the overlay can omit the row entirely.
   */
  area: number | null;
};

export const EMPTY_SUMMARY: SelectionSummary = {
  count: 0,
  length: 0,
  area: null,
};

/**
 * How many connected runs a set of chunks forms.
 *
 * Flood-fills through the welded graph, never leaving the set — two selected
 * chunks that merely both touch a third, unselected one are two runs, not one.
 *
 * `keyOf` is the same hook `walkChain` takes: adjacent selected chunks whose
 * keys differ (a role boundary) stay separate runs, even when they share a
 * welded node. Omitted, every neighbour in the set chains, which is the right
 * answer for an unmapped drawing.
 */
const countRuns = (
  chunks: ReadonlyMap<string, ChunkRef>,
  chunkGraph: ChunkGraph,
  keyOf?: (chunk: ChunkRef) => string | null,
): number => {
  const keyFor = (chunk: ChunkRef) => selectionKey({ kind: 'edge', ...chunk });
  const unvisited = new Set(chunks.keys());
  let runs = 0;

  while (unvisited.size > 0) {
    const seedKey = unvisited.values().next().value;
    if (seedKey === undefined) {
      break;
    }
    const seed = chunks.get(seedKey);
    unvisited.delete(seedKey);
    if (!seed) {
      continue;
    }
    runs += 1;
    const frontier: ChunkRef[] = [seed];

    while (frontier.length > 0) {
      const chunk = frontier.pop();
      if (!chunk) {
        break;
      }
      const ends = chunkGraph.endsOf(chunk);
      if (!ends) {
        continue;
      }
      [ends.start, ends.end].forEach((node) =>
        chunkGraph.chunksAt(node).forEach((neighbour) => {
          const key = keyFor(neighbour);
          if (!unvisited.has(key)) {
            return;
          }
          if (keyOf) {
            const from = keyOf(chunk);
            const to = keyOf(neighbour);
            if (from === null || to === null || from !== to) {
              return;
            }
          }
          unvisited.delete(key);
          frontier.push(neighbour);
        }),
      );
    }
  }

  return runs;
};

/**
 * Measures a selection.
 *
 * `count` is how many *things* are selected, not how many refs: one click on a
 * profile can return a dozen edge refs that the user experiences as a single
 * contour, and reporting "12" for it would be wrong. Given the welded graph,
 * refs are expanded to chunks and regrouped into connected runs, and it is the
 * runs that are counted — so that dozen reads as one, and a whole stitched loop
 * still reads as one because its chunks are connected too.
 *
 * Without a graph there is no way to tell which chunks join, so the count falls
 * back to the number of refs — the right answer for every caller that selects
 * whole loops.
 *
 * `keyOf` splits runs the same way `walkChain` does: adjacent selected chunks
 * whose keys differ stay separate, so two role features that happen to share a
 * welded node still count as two.
 *
 * Area comes only from whole closed loops. An open run encloses nothing, and a
 * run assembled across several stitched loops that happens to close again is
 * deliberately not measured: establishing that it really is a simple ring costs
 * an ordered traversal, and nothing in the viewer asks for the area of one.
 */
export const summarizeSelection = (
  selection: Selection,
  loops: readonly Loop[],
  chunkGraph?: ChunkGraph,
  keyOf?: (chunk: ChunkRef) => string | null,
): SelectionSummary => {
  const byId = new Map(loops.map((loop) => [loop.id, loop]));
  let length = 0;
  let area: number | null = null;
  /** Refs that resolved to real geometry — the fallback count. */
  let refCount = 0;

  /** Every chunk the selection covers, however it was addressed. */
  const chunks = new Map<string, ChunkRef>();

  selection.forEach((ref) => {
    const loop = byId.get(ref.loopId);
    if (!loop) {
      return;
    }
    refCount += 1;

    if (ref.kind === 'edge') {
      const chunk = loop.chunks[ref.chunkIndex];
      if (!chunk) {
        refCount -= 1;
        return;
      }
      const key = selectionKey(ref);
      if (!chunks.has(key)) {
        length += chunk.length;
        chunks.set(key, {
          loopId: ref.loopId,
          chunkIndex: ref.chunkIndex,
        });
      }
      return;
    }

    if (loop.isClosed && loop.area !== null) {
      area = (area ?? 0) + loop.area;
    }
    loop.chunks.forEach((chunk) => {
      const key = edgeKeyOf(loop.id, chunk.index);
      if (chunks.has(key)) {
        return;
      }
      length += chunk.length;
      chunks.set(key, { loopId: loop.id, chunkIndex: chunk.index });
    });
  });

  return {
    count: chunkGraph ? countRuns(chunks, chunkGraph, keyOf) : refCount,
    length,
    area,
  };
};

/** The edge a drill-down last opened, or `null` when nothing is drilled into. */
export type DrilledEdge = {
  readonly loopId: number;
  readonly chunkIndex: number;
} | null;

/**
 * Alternates between the edge under the pointer and the loop that owns it.
 *
 * Driven by an explicit memory of what was last drilled into rather than by
 * reading the current selection, because the single clicks that make up a
 * double-click land first and leave the loop selected — inferring from the
 * selection would therefore see "loop" every time and never come back out.
 *
 * Returns both the ref to select and the memory to carry forward, so the caller
 * holds no rule of its own.
 */
export const drillSelection = (
  hit: { loopId: number; chunkIndex: number },
  drilled: DrilledEdge,
): { ref: SelectionRef; drilled: DrilledEdge } => {
  const isSameEdge =
    drilled !== null &&
    drilled.loopId === hit.loopId &&
    drilled.chunkIndex === hit.chunkIndex;

  return isSameEdge
    ? { ref: { kind: 'loop', loopId: hit.loopId }, drilled: null }
    : {
        ref: {
          kind: 'edge',
          loopId: hit.loopId,
          chunkIndex: hit.chunkIndex,
        },
        drilled: { loopId: hit.loopId, chunkIndex: hit.chunkIndex },
      };
};

/** Every loop with at least one chunk drawn on `layerIndex`. */
export const loopsOnLayer = (
  loops: readonly Loop[],
  layerIndex: number,
): SelectionRef[] =>
  loops
    .filter((loop) => loop.layers.has(layerIndex))
    .map((loop) => ({ kind: 'loop', loopId: loop.id }));

/**
 * Flattens a selection into segment endpoint pairs for the accent overlay:
 * `[x0,y0,z0, x1,y1,z1, …]`, ready for a stroke batch.
 *
 * Hidden chunks are skipped, so painting can never reveal geometry the user turned
 * off — the same rule picking follows.
 */
export const selectionPositions = (
  selection: Selection,
  loops: readonly Loop[],
  hiddenLayers?: ReadonlySet<number>,
): number[] => {
  const byId = new Map(loops.map((loop) => [loop.id, loop]));
  const positions: number[] = [];

  const pushChunk = (points: readonly (readonly [number, number])[]) => {
    for (let index = 1; index < points.length; index += 1) {
      positions.push(
        points[index - 1][0],
        points[index - 1][1],
        0,
        points[index][0],
        points[index][1],
        0,
      );
    }
  };

  selection.forEach((ref) => {
    const loop = byId.get(ref.loopId);
    if (!loop) {
      return;
    }
    const chunks =
      ref.kind === 'edge'
        ? [loop.chunks[ref.chunkIndex]].filter(Boolean)
        : loop.chunks;

    chunks.forEach((chunk) => {
      if (hiddenLayers?.has(chunk.layer)) {
        return;
      }
      pushChunk(chunk.points);
    });
  });

  return positions;
};
